import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { useRouter } from 'next/router';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import {
    Button, Col,
    Text,
} from 'reaxl';
import {
    sendClick,
    sendImpressions,
} from 'reaxl-analytics';

import { getFilterSessionKey, getFirstUnseenVehicleFeature, markFilterAsSeen } from '@/utilities/filterSuggestionHierarchy';

import { inlineFilterPromoClick, inlineFilterPromoImpression } from '@/analytics/srp/srpSearchEvents';

import { getFeatureDescription } from '@/config/vehicleFeatureDescriptions';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import TruckCabSizeFilter from '@/components/filters/TruckCabSizeFilter';
import PromoCard from '@/components/PromoCard/PromoCard';

/**
 * Generate card configuration based on hierarchy data
 */
// Returns the first unseen filter card config in priority order
const getInlineFilterCardConfig = (hierarchyData) => {
    // Safety check for hierarchyData
    if (!hierarchyData) {
        return null;
    }

    // Priority 1: Strong Attributes
    if (hierarchyData.isStrongAttribute && hierarchyData.firstStrongAttribute) {
        return {
            type: 'strongAttribute',
            promptText: `${hierarchyData.firstStrongAttribute?.name}?`,
            filterValue: hierarchyData.firstStrongAttribute?.codes,
            showPrefixText: true,
            sessionKey: getFilterSessionKey({ isStrongAttribute: true, firstStrongAttribute: hierarchyData.firstStrongAttribute }),
        };
    }

    // Priority 2: Truck
    if (hierarchyData.isTruckCard) {
        return {
            type: 'truck',
            promptText: 'How many passenger seats do you need in your truck?',
            filterValue: {},
            showPrefixText: false,
            sessionKey: getFilterSessionKey({ isTruckCard: true }),
        };
    }

    // Priority 2b: SUV
    if (hierarchyData.isSUVCard) {
        return {
            type: 'suv',
            promptText: 'Do you need 3rd row seating?',
            filterValue: { featureCode: ['1306'] },
            showPrefixText: false,
            sessionKey: getFilterSessionKey({ isSUVCard: true }),
        };
    }

    // Priority 3: Vehicle Features - check for unseen features dynamically (regardless of hierarchy flags)
    if (!hierarchyData.isStrongAttribute && !hierarchyData.isTruckCard && !hierarchyData.isSUVCard) {
        // Check if there are available vehicle features
        const availableFeatures = hierarchyData.availableVehicleFeatures || [];
        const firstUnseenFeature = getFirstUnseenVehicleFeature(availableFeatures);

        if (firstUnseenFeature) {
            return {
                type: 'vehicleFeature',
                promptText: `${firstUnseenFeature?.label}?`,
                filterValue: { featureCode: [firstUnseenFeature?.value] },
                showPrefixText: true,
                sessionKey: getFilterSessionKey({ isVehicleFeatureCard: true, randomVehicleFeature: firstUnseenFeature }),
            };
        }
    }

    // Priority 4: Suggested Filters - check if there are suggested filters available when higher priorities are exhausted
    if (hierarchyData.firstSuggestedFilter) {
        return {
            type: 'suggested',
            promptText: `${hierarchyData.firstSuggestedFilter?.name}?`,
            filterValue: hierarchyData.firstSuggestedFilter?.codes,
            showPrefixText: true,
            sessionKey: getFilterSessionKey({ isSuggestedFilter: true, firstSuggestedFilter: hierarchyData.firstSuggestedFilter }),
        };
    }

    return null;
};

const getFilterDataCmp = ({
    firstStrongAttribute,
    isTruckCard,
    isSUVCard,
    isVehicleFeatureCard,
} = {}) => {
    if (firstStrongAttribute) return 'filter-inline-strong';
    if (isTruckCard) return 'filter-inline-truck';
    if (isSUVCard) return 'filter-inline-suv';
    if (isVehicleFeatureCard) return 'filter-inline-feature';
    return 'filter-inline-suggested';
};

/**
 * Renders Inline Filter Promo Card in priority order:
 * 1) Strong Attribute filter
 * 2) Truck/SUV Bodystyle substyle (Truck cab size filter, SUV 3rd row filter)
 * 3) Vehicle Features
 * 4) Suggested filter
 */
function InlineFilterPromoCard({ hierarchyData }) {
    const router = useRouter();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const navigateToSrp = useSrpNavigation();

    // State to force re-render when filters are marked as seen
    const [renderKey, setRenderKey] = useState(0);
    const {
        srp_inline_filters: [, { track_seen_suggestions: trackSeenSuggestions }],
    } = useFeatures(['srp_inline_filters']);

    // Get the first unseen card config
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cardConfig = useMemo(() => getInlineFilterCardConfig(hierarchyData), [hierarchyData, renderKey]);
    const sessionKey = cardConfig?.sessionKey;
    const filterDataCmp = useMemo(() => getFilterDataCmp(hierarchyData || {}), [hierarchyData]);
    const shouldRenderCard = !!cardConfig;
    const { isTruckCard = false } = hierarchyData || {};

    const prevUrlRef = useRef(null);

    const impressionData = useMemo(() => ({
        name: inlineFilterPromoImpression,
        data: {
            pixallData: {
                cmp: filterDataCmp,
                featureName: cardConfig?.promptText || '',
            },
        },
    }), [cardConfig?.promptText, filterDataCmp]);

    // Mark filter as seen and fire impression only once per session per filter
    useEffect(() => {
        const currentUrl = router.asPath;
        if (prevUrlRef.current !== currentUrl && shouldRenderCard && sessionKey && trackSeenSuggestions) {
            markFilterAsSeen(sessionKey);
        }
        // To make sure the impression only fires 1 time after card re-renders based on URL changes
        if (prevUrlRef.current !== currentUrl && shouldRenderCard) {
            sendImpressions(impressionData);
            prevUrlRef.current = currentUrl;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath, impressionData, shouldRenderCard]);

    const handleButtonClick = useCallback(async (event, filterValue) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        sendClick(inlineFilterPromoClick, event, {
            pixallData: {
                cmp: filterDataCmp,
                featureName: cardConfig?.promptText || '',
            },
        });

        // Mark current filter as seen so user doesn't see it again
        if (sessionKey) {
            markFilterAsSeen(sessionKey);
        }

        await navigateToSrp({
            resetPagination: true,
            scroll: true,
            isNewSearch: true,
            filtersValues: filterValue,
        });
    }, [filterDataCmp, cardConfig?.promptText, sessionKey, navigateToSrp]);

    const handleNoPreferenceButtonClick = useCallback(async (event) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        sendClick(inlineFilterPromoClick, event, {
            pixallData: {
                cmp: filterDataCmp,
                featureName: cardConfig?.promptText || '',
                isNoPreference: true,
            },
        });

        // Mark current filter as seen so the next filter can be shown
        if (sessionKey) {
            markFilterAsSeen(sessionKey);
            // Force re-render to show next filter
            setRenderKey((prev) => prev + 1);
        }
    }, [cardConfig?.promptText, filterDataCmp, sessionKey]);

    const callToAction = useCallback((filterValue, cardType) => {
        if (isTruckCard) {
            return (
                <TruckCabSizeFilter
                    enableDropdown
                    onOptionChange={(event) => handleButtonClick(event, { bodyStyleSubtypeCode: event?.target?.value })}
                />
            );
        }
        if (cardType === 'vehicleFeature') {
            return (
                <div className={clsx('margin-top-4', {
                    'display-flex': isXs,
                    'display-flex flex-column': !isXs,
                })}
                >
                    <Button
                        bsSize="small"
                        bsStyle="primary"
                        onClick={(event) => handleButtonClick(event, filterValue)}
                        className={clsx({
                            'margin-right-2 flex-1': isXs,
                            'margin-bottom-4 width-100': !isXs,
                        })}
                    >
                        Yes, Update Filter
                    </Button>
                    <Button
                        bsSize="small"
                        bsStyle="default"
                        onClick={(event) => handleNoPreferenceButtonClick(event)}
                        className={clsx({
                            'flex-1': isXs,
                            'width-100': !isXs,
                        })}
                    >
                        No Preference
                    </Button>
                </div>
            );
        }

        return (
            <Button
                bsSize="small"
                bsStyle="primary"
                onClick={(event) => handleButtonClick(event, filterValue)}
                className="margin-top-4"
            >
                Yes, Update My Search
            </Button>
        );
    }, [isTruckCard, handleButtonClick, handleNoPreferenceButtonClick, isXs]);

    // Early return if no hierarchyData is provided
    if (!hierarchyData) {
        return null;
    }

    const renderCardBody = (promptText, filterValue, showPrefixText = false, cardType = null) => {
        if (cardType === 'vehicleFeature') {
            // Extract feature name from prompt text (remove "Do you want " and "?")
            const featureName = promptText.replace('Do you want ', '').replace('?', '');
            const featureDescription = getFeatureDescription(featureName);

            return (
                <>
                    <Text
                        color="primary"
                        componentClass="div"
                        size={200}
                    >
                        Do you want
                    </Text>
                    <Text
                        className="margin-top-1"
                        color="primary"
                        componentClass="div"
                        size={400}
                        weight="bold"
                    >
                        {featureName}
                        ?
                    </Text>
                    {featureDescription && (
                        <Text
                            className="margin-top-2"
                            color="neutral-secondary"
                            componentClass="div"
                            size={200}
                        >
                            {featureDescription}
                        </Text>
                    )}
                    {callToAction(filterValue, cardType)}
                </>
            );
        }

        return (
            <>
                {showPrefixText && (
                    <Text
                        className="margin-top-3"
                        color="primary"
                        componentClass="div"
                    >
                        {cardType === 'vehicleFeature' ? 'Do you want' : 'Only interested in vehicles that are'}
                    </Text>
                )}
                <Text
                    className={clsx({ 'margin-top-3': !showPrefixText })}
                    color="primary"
                    componentClass="div"
                    size={400}
                    weight="bold"
                >
                    {promptText}
                </Text>

                {callToAction(filterValue, cardType)}
            </>
        );
    };

    // Early return if card shouldn't render or all filters have been seen
    if (!shouldRenderCard || !cardConfig) {
        return null;
    }

    return (
        <Col
            componentClass={PromoCard}
            xs={12}
            sm={6}
            md={4}
            className="display-flex margin-bottom-5"
            data-cmp={filterDataCmp}
            panelClassName="bg-brand-secondary"
        >
            {renderCardBody(cardConfig.promptText, cardConfig.filterValue, cardConfig.showPrefixText, cardConfig.type)}
        </Col>
    );
}

InlineFilterPromoCard.propTypes = {
    hierarchyData: PropTypes.object.isRequired,
};

export default memo(InlineFilterPromoCard);
